import { Box, Button, Dialog, DialogContent } from "@mui/material";
import React, { useEffect } from "react";
import {
  getExistingUserError,
  IUserInvModal,
  UserInvitationType,
} from "./api/types";
import useUserInvStore from "./api/store";
import { Handlers } from "./api/handlers";
import { ModalTransition } from "../transitions";
import StyledDialogTitle from "../dialog/dialog-title";
import { ModalType } from "../user-invitation-modal/api/types";
import { DataTable } from "./components/data-table";
import { userFormSchema, UserFormSchema, UserSchema } from "./api/schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetPagedUsersByEmail, useGetPagedUsersByName } from "./api/hooks";
import { QueryForm } from "./components/query-form";
import StyledDialogActions from "../dialog/dialog-actions";
import { commonButtonProps } from "../buttons/sxprops";
import { PagedUserModel } from "../../../main/users-facility/api/types";
import { AppSnackBarHandlers } from "../app-snackbar/handlers";
import useFacilityStore from "../../../main/facilities/api/store";
import useWalkthroughState from "../walkthrough/api/store";
import { getDummyPaged } from "../../../main/users/components/walkthrough-types";
import { enqueueSnackbar } from "notistack";

export const UserInvModal: React.FC<IUserInvModal> = ({
  onInviteNew,
  onSelectActiveRecord,
  onClose,
  onNoResultFound,
  formSchema,
  walkthroughMode,
  isInternal,
  modalTitle = "User Invitation",
  formTitle = "Invite User",
  formDescription = "Please fill in the details below to invite a user.",
}) => {
  const appSnackBarHandlers = new AppSnackBarHandlers();

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
    reset,
  } = formSchema;
  const userInvStore = useUserInvStore();

  const { setIsChangeAdminOpen } = useFacilityStore();

  const userForm = useForm<UserFormSchema>({
    // mode: 'onBlur',
    resolver: zodResolver(userFormSchema),
  });

  const getUserByEmail = useGetPagedUsersByEmail(walkthroughMode ?? false);
  const getUserByName = useGetPagedUsersByName();

  const handlers = new Handlers();

  const { step, selectedStep, openWalkthrough } = useWalkthroughState();
  const isWTStep4 =
    step === 4 && selectedStep === "wt-invite-dialog-2";

  const queryType = watch("type");
  const pageSize = userInvStore.pageSize ?? 6;
  const pageNumber = userInvStore.pageNumber ?? 1;

  const data: PagedUserModel | undefined = isWTStep4
    ? getDummyPaged(pageNumber, pageSize)
    : queryType === UserInvitationType.Email
      ? getUserByEmail.data
      : getUserByName.data;

  const isFetching = isWTStep4
    ? false
    : queryType === UserInvitationType.Email
      ? getUserByEmail.isPending
      : getUserByName.isPending;

  useEffect(() => {
    if (walkthroughMode || userInvStore.walkthroughMode) {
      setValue("email", isInternal ? "juanreyes@mailinator.com" : "johndoe@mailinator.com");
    } else {
      setValue("email", "");
    }
  }, [userInvStore.walkthroughMode, walkthroughMode, setValue, isInternal]);

  // Refetch data when page number changes
  useEffect(() => {
    if (userInvStore.modalType !== ModalType.QueryResult) return;
    if (isWTStep4) return;

    if (queryType === UserInvitationType.Email && userInvStore.queryData?.email) {
      getUserByEmail.mutate();
    } else if (
      queryType === UserInvitationType.UserFullname &&
      userInvStore.queryData?.firstName &&
      userInvStore.queryData?.lastName
    ) {
      getUserByName.mutate();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInvStore.pageNumber, pageNumber]);

  React.useEffect(() => {
    if (!isWTStep4) return;

    // show the query form first so submit makes sense
    if (userInvStore.modalType !== ModalType.QueryForm) {
      userInvStore.setModalType(ModalType.QueryForm);
    }

    // prefill with a valid email query
    setValue("type", UserInvitationType.Email, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("email", "johndoe@mailinator.com", {
      shouldValidate: true,
      shouldDirty: true,
    });

    // programmatically submit (equivalent to clicking "Continue")
    handleSubmit(handleFormSubmit)();

    // ensure the UI shows the results screen
    userInvStore.setModalType(ModalType.QueryResult);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWTStep4]);
  // ─────────────────────────────────────────────────────────────

  // disable actions while fetching (unless in walkthrough step 3)
  const disabled = isFetching;

  const handleFormSubmit = (query: UserSchema) => {
    userInvStore.setQueryData(query);
    userInvStore.setPageNumber(1);
    userInvStore.setSelectedUser(undefined);

    // During the walkthrough, bypass network & "no results" snackbar
    if (isWTStep4) {
      userInvStore.setModalType(ModalType.QueryResult);
      return;
    }

    if (queryType === UserInvitationType.Email) {
      if (!query.email) {
        console.error("Email is required for this query type.");
        return;
      }
      getUserByEmail.mutate(undefined, {
        onSuccess: (resp) => {
          if ((resp?.totalItems ?? 0) <= 0) {
            const formattedUser: UserFormSchema = {
              id: "",
              email: query.email ?? "",
              lastName: "",
              firstName: "",
              middleName: "",
              suffix: "",
              prefix: "",
              customName: "",
              civilStatus: 0,
              gender: 0,
              productCode: undefined,
              facilityCode: undefined,
              role: { id: "", name: "", displayName: "", role: "" },
              clientIds: [],
              birthDate: "",
              photoUrl: undefined,
              isDefaultPassword: true,
            };
            onNoResultFound?.(formattedUser);
            appSnackBarHandlers.handlePopIn(
              "No Resuts Found",
              "We couldn't find any matching results based on your search",
            );
          }
        },
      });
    } else if (queryType === UserInvitationType.UserFullname) {
      if (!query.firstName || !query.lastName) {
        console.error(
          "First Name and Last Name are required for this query type.",
        );
        return;
      }
      getUserByName.mutate(undefined, {
        onSuccess: (resp) => {
          if ((resp?.totalItems ?? 0) <= 0) {
            const formattedUser: UserFormSchema = {
              id: "",
              email: "",
              lastName: query.lastName ?? "",
              firstName: query.firstName ?? "",
              middleName: "",
              suffix: "",
              prefix: "",
              customName: "",
              civilStatus: 0,
              gender: 0,
              productCode: undefined,
              facilityCode: undefined,
              role: { id: "", name: "", displayName: "", role: "" },
              clientIds: [],
              birthDate: "",
              photoUrl: undefined,
              isDefaultPassword: true,
            };
            onNoResultFound?.(formattedUser);
            appSnackBarHandlers.handlePopIn(
              "No Resuts Found",
              "We couldn't find any matching results based on your search",
            );
          }
        },
      });
    } else {
      console.error("Invalid query type");
      return;
    }

    userInvStore.setModalType(ModalType.QueryResult);
  };

  return (
    <Dialog
      hideBackdrop={openWalkthrough || userInvStore.walkthroughMode}
      open={userInvStore.isModalOpen}
      onClose={() => {
        handlers.handleCloseModal(reset, onClose);
        setIsChangeAdminOpen(false);
      }}
      maxWidth={"desktop"}
      slots={{
        transition: ModalTransition,
      }}
      slotProps={{
        paper: {
          id: "wt-invite-dialog",
          "data-testid": "wt-invite-dialog",
          component: "form",
          onSubmit: (event: React.FormEvent) => {
            event.preventDefault();
            handleSubmit(handleFormSubmit)();
          },
          sx: { borderRadius: "24px" },
        } as any,
      }}
      keepMounted
      aria-describedby="alert-dialog-slide-description"
    >
      <Box id="invite-user-search-modal">
        <StyledDialogTitle
          actionType={
            userInvStore.modalType === ModalType.QueryResult ||
            userInvStore.modalType === ModalType.UserForm
              ? "back"
              : "close"
          }
          handleBack={handlers.handleBack}
          testIdPrefix="user-inv-modal"
          title={userInvStore.headerTitle()}
          subtitle={userInvStore.headerSubtitle()}
          handleClose={() => handlers.handleCloseModal(reset, onClose)}
        />

        <DialogContent sx={{ minWidth: "450px", mt: "28px" }}>
          {userInvStore.modalType === ModalType.QueryResult && (
            <Box
              sx={{
                width: "100%",
              }}
            >
              <DataTable
                data={data}
                isFetching={isFetching}
                pageNumber={userInvStore.pageNumber}
                pageSize={userInvStore.pageSize}
                selectedUser={userInvStore.selectedUser}
                setSelectedUser={userInvStore.setSelectedUser}
                setPageNumber={userInvStore.setPageNumber}
              />
            </Box>
          )}
          {userInvStore.modalType === ModalType.QueryForm && (
            <QueryForm
              control={control}
              errors={errors}
              disabled={disabled}
              setValue={setValue}
              watch={watch}
            />
          )}
        </DialogContent>

        {userInvStore.modalType === ModalType.QueryForm && (
          <StyledDialogActions>
            <Button
              id="user-inv-modal-cancel-button"
              data-testid="user-inv-modal-cancel-button"
              disabled={disabled}
              onClick={() => handlers.handleCloseModal(reset, onClose)}
            >
              Cancel
            </Button>
            <Button
              id="user-inv-modal-continue-button"
              data-testid="user-inv-modal-continue-button"
              ref={userInvStore.searchButtonRef}
              disabled={disabled}
              loading={isFetching}
              type="submit"
              sx={{
                ...commonButtonProps,
                width: "105px",
              }}
            >
              Continue
            </Button>
          </StyledDialogActions>
        )}

        {userInvStore.modalType === ModalType.QueryResult && (
          <StyledDialogActions>
            <Button
              id="user-inv-modal-ignore-button"
              data-testid="user-inv-modal-ignore-button"
              disabled={userInvStore.clearingUserForm}
              onClick={() => handlers.handleInviteNew(queryType, onInviteNew)}
              sx={{
                ...commonButtonProps,
                width: "200px",
              }}
            >
              Ignore and Invite New
            </Button>
            <Button
              id="user-inv-modal-select-active-button"
              data-testid="user-inv-modal-select-active-button"
              ref={userInvStore.selectActiveRecordButtonRef}
              disabled={
                !userInvStore.selectedUser || userInvStore.clearingUserForm
              }
              //loading={userInvStore.clearingUserForm}
              onClick={() => {
                const userError = getExistingUserError(
                  userInvStore?.selectedUser,
                  userInvStore?.isAddingFacilityUser,
                  userInvStore?.isAddingBizBoxUser,
                  userInvStore?.isAddingGroupAdmin,
                  userInvStore?.isFacilityUsersModule,
                );
                if (userError)
                  return enqueueSnackbar(userError, { variant: "error" });
                handlers.handleSelectActiveRecord(
                  userForm,
                  onSelectActiveRecord,
                );
              }}
              sx={{
                ...commonButtonProps,
                width: "200px",
                backgroundColor: "primary.main",
              }}
            >
              Select Active Record
            </Button>
          </StyledDialogActions>
        )}
      </Box>
    </Dialog>
  );
};
